import _ from 'lodash';
import { Expansions } from '../../../../../shared/common/enums';
import { processCharacterSpecialItems } from '../../../../../shared/frontend/scripts/wow/gearHelpers';
import { RAID_METADATA_BY_SLUG } from '../../../shared/enums';
import { TBonusesByTier } from '../components/container/encounterDetailsTooltipContent';
import { sortCharactersRoster } from '../../../../../shared/common/character';
import { IGetGroupCompositionCompareItem, TEncounterBossKillRosterMember } from '../../../shared/types/raidTypes';
import { useEffect } from 'react';

// iframe height used when the comp compare page is embedded via [embed] BBCode / widget URL
export const GROUP_COMPOSITION_COMPARE_EMBED_HEIGHT = 1070;

export function shouldShowDominationShards(raidSlug: string) {
  return raidSlug === 'sanctum-of-domination';
}
export function shouldShowTier(raidSlug: string) {
  const expansionId = RAID_METADATA_BY_SLUG[raidSlug]?.expansion;
  return (
    shouldShowSepulcherTierSummary(raidSlug) ||
    expansionId === Expansions.Legion ||
    expansionId === Expansions.Dragonflight ||
    expansionId === Expansions.TheWarWithin
  );
}

export function shouldShowAzeriteGear(raidSlug: string) {
  return RAID_METADATA_BY_SLUG[raidSlug]?.expansion === Expansions.BattleForAzeroth;
}

export function shouldShowEssences(raidSlug: string) {
  return raidSlug === 'the-eternal-palace' || raidSlug === 'nyalotha-the-waking-city';
}

export function shouldShowTalents(raidSlug: string) {
  const raid = RAID_METADATA_BY_SLUG[raidSlug];
  if (!raid) {
    return false;
  }
  return raid.expansion >= Expansions.BattleForAzeroth && raid.expansion < Expansions.Dragonflight;
}

export function shouldShowDFTalents(raidSlug: string) {
  const raid = RAID_METADATA_BY_SLUG[raidSlug];
  if (!raid) {
    return false;
  }
  return raid.expansion >= Expansions.Dragonflight;
}

export function shouldShowCovenant(raidSlug: string) {
  return RAID_METADATA_BY_SLUG[raidSlug]?.expansion === Expansions.Shadowlands;
}

export function shouldShowLegendaries(raidSlug: string) {
  const raid = RAID_METADATA_BY_SLUG[raidSlug];
  if (!raid) {
    return false;
  }
  return raid.expansion === Expansions.Shadowlands || raid.expansion === Expansions.Legion;
}

export function shouldShowCorruption(raidSlug: string) {
  return raidSlug === 'nyalotha-the-waking-city';
}

export function shouldShowRaidGraphs(raidSlug: string, guildPrivacy: any) {
  if (!RAID_METADATA_BY_SLUG[raidSlug]?.supportsGraphs) {
    return false;
  }

  if (guildPrivacy && guildPrivacy.shareRaidUntil) {
    return true;
  }

  return (
    guildPrivacy &&
    guildPrivacy.raidPercents &&
    guildPrivacy.raidPulls
  );
}

export function shouldShowArtifactTraits(raidSlug: string) {
  return RAID_METADATA_BY_SLUG[raidSlug]?.expansion === Expansions.Legion;
}

export function getModifiedArtifactTraits(raidSlug: string, value: number) {
  if (RAID_METADATA_BY_SLUG[raidSlug]?.expansion === Expansions.Legion) {
    return value * 0xffff;
  }
  return value;
}

export function shouldShowDominationItems(raidSlug: string) {
  return raidSlug === 'sanctum-of-domination';
}

export function shouldShowSepulcherTierSummary(raidSlug: string) {
  return [
    'sepulcher-of-the-first-ones',
    'fated-castle-nathria',
    'fated-sanctum-of-domination',
    'fated-sepulcher-of-the-first-ones'
  ].includes(raidSlug);
}

export function getRosterSummaries(roster: TEncounterBossKillRosterMember[]) {
  const summaries = roster.reduce(
    (acc: any, { character }: any) => {
      if (character.spec.role === 'dps') {
        acc.dps++;
        if (character.spec.is_melee) {
          acc.meleeDps++;
        } else {
          acc.rangedDps++;
        }
      } else if (character.spec.role === 'healer') {
        acc.healing++;
      } else if (character.spec.role === 'tank') {
        acc.tank++;
      } else {
        acc.unknown++;
      }

      if (character.artifactTraits > 0) {
        acc.artifactTraits += character.artifactTraits;
        acc.validArtifactTraits++;
      }

      const specialItems = processCharacterSpecialItems(character.items.items);
      if (specialItems.heartLevel > 0) {
        acc.heartLevel += specialItems.heartLevel;
        acc.validHeartLevels++;
      }

      if (specialItems.dominationShards.length > 0) {
        acc.validShards += specialItems.dominationShards.length;
        for (const shard of specialItems.dominationShards) {
          if (!acc.shards[shard.name]) {
            acc.shards[shard.name] = 0;
          }
          acc.shards[shard.name]++;
        }
      }

      if (character.itemLevelEquipped > 0) {
        acc.itemLevelEquipped += character.itemLevelEquipped;
        acc.validItemLevelEquipped++;
      }

      return acc;
    },
    {
      dps: 0,
      meleeDps: 0,
      rangedDps: 0,
      healing: 0,
      tank: 0,
      unknown: 0,
      artifactTraits: 0,
      validArtifactTraits: 0,
      itemLevelEquipped: 0,
      validItemLevelEquipped: 0,
      heartLevel: 0,
      validHeartLevels: 0,
      validShards: 0,
      shards: {}
    }
  );

  if (summaries.validArtifactTraits > 0) {
    summaries.artifactTraits /= summaries.validArtifactTraits;
  } else {
    summaries.artifactTraits = 0;
  }

  if (summaries.validHeartLevels > 0) {
    summaries.heartLevel /= summaries.validHeartLevels;
  } else {
    summaries.heartLevel = 0;
  }

  if (summaries.validItemLevelEquipped > 0) {
    summaries.itemLevelEquipped /= summaries.validItemLevelEquipped;
  } else {
    summaries.itemLevelEquipped = 0;
  }

  const validTiers: string[] = [];
  const bonusesByTier: TBonusesByTier = {};

  for (const { character } of roster) {
    const tierCounts: any = {};
    for (const [, item] of _.toPairs<any>(character.items?.items ?? {})) {
      if (item.tier) {
        const tier = item.tier;
        if (!bonusesByTier[tier]) {
          bonusesByTier[tier] = { 1: 0, 2: 0, 3: 0, 4: 0 };
        }

        if (!tierCounts[tier]) {
          tierCounts[tier] = 0;
        }
        tierCounts[tier]++;
      }
    }

    for (const [tier, count] of _.toPairs<any>(tierCounts)) {
      if (count === 0) {
        continue;
      }

      if (count >= 4) {
        bonusesByTier[tier][4]++;
      } else {
        bonusesByTier[tier][count]++;
      }

      const tierNumber = tier.toString();
      if (validTiers.indexOf(tierNumber) === -1) {
        validTiers.push(tierNumber);
      }
    }
  }

  validTiers.sort();

  return { summaries, validTiers, bonusesByTier };
}

export function sortRoster(a: TEncounterBossKillRosterMember, b: TEncounterBossKillRosterMember) {
  return sortCharactersRoster(a.character, b.character);
}

export const EMPTY_GROUP_COMPOSITION_COMPARE_ITEM: IGetGroupCompositionCompareItem = {
  name: '',
  realm: '',
  region: '',
  raid: '',
  difficulty: '',
  boss: ''
};

export const useFullContainerWidth = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const container = document.querySelector('#main-content-container') as HTMLElement;
    const header = document.querySelector('#rio-desktop-nav-menu-container') as HTMLElement;

    if (!container) {
      return;
    }

    const updateClasses = () => {
      container.classList.add('main-content-full-width');
      if (header) {
        header.classList.add('slds-container--x-large', 'slds-container--center');
      }
    };

    const removeClasses = () => {
      container.classList.remove('main-content-full-width');
      if (header) {
        header.classList.remove('slds-container--x-large', 'slds-container--center');
      }
    };

    updateClasses();

    const resizeObserver = new ResizeObserver(() => {
      updateClasses();
    });

    resizeObserver.observe(container);

    return () => {
      removeClasses();
      resizeObserver.disconnect();
    };
  }, [enabled]);
};
